import type {BackgroundLayout, BgColor} from '../../lib/lvs-foni'
import type {ArrowPlacement} from '../../lib/sign-renderer'
import {ANNOTATION} from '../../lib/annotation-inks'
import {lvsRef, round4} from '../../lib/utils'
import {HorizDim, VertDim} from '../ui/dimension-line'
import {SignBody} from './SignBody'

const SCALE = 0.25
const PAD = 40
const RIGHT_GUTTER = 220
const BOTTOM_GUTTER = 24

// Padding-zone tint — radius-orange at 12% alpha, local to this
// 0.5h annotation.
const CLR_PAD = 'rgba(192,88,0,0.12)'

export interface FoniCanvasProps {
    readonly layout: BackgroundLayout
    readonly bgColor: BgColor
    readonly strike: boolean
    /** Optional arrow placement (sign 705). Forwarded to SignBody. */
    readonly arrow?: ArrowPlacement
    /** Unique id suffix for clipPaths. Defaults to "foni". */
    readonly idSuffix?: string
    /** Hide the right-gutter R/Kontrastapmale/Melnā mala/D callouts. */
    readonly hideCallouts?: boolean
}

/**
 * Scaled, annotated preview of a foni sign. The canonical foni canvas
 * used by FoniPage directly and by the konstruktors wizard's PreviewStep.
 * Composes SignBody for the clean sign render and adds dimension lines,
 * baselines, padding tint, corner-radius indicator, and right-gutter
 * callouts on top.
 */
export function FoniCanvas({
    layout,
    bgColor,
    strike,
    arrow,
    idSuffix = 'foni',
    hideCallouts = false
}: FoniCanvasProps) {
    const margin = 0.5 * layout.maxRowHeight
    const gutter = hideCallouts ? 0 : RIGHT_GUTTER
    const svgW = layout.width * SCALE + 2 * PAD + gutter
    const svgH = layout.height * SCALE + 2 * PAD + BOTTOM_GUTTER

    return (
        <div className='overflow-x-auto border border-foreground/10 bg-surface-container-highest'>
            <svg
                width={svgW}
                height={svgH}
                style={{display: 'block'}}
            >
                <g transform={`translate(${PAD} ${PAD}) scale(${SCALE})`}>
                    <SignBody
                        layout={layout}
                        bgColor={bgColor}
                        strike={strike}
                        arrow={arrow}
                        idSuffix={idSuffix}
                    />

                    {/* 0.5h padding zone tint */}
                    {margin > 0 && (
                        <g>
                            <rect
                                x={layout.innerBox.x}
                                y={layout.innerBox.y}
                                width={layout.innerBox.width}
                                height={margin}
                                fill={CLR_PAD}
                            />
                            <rect
                                x={layout.innerBox.x}
                                y={layout.innerBox.y + layout.innerBox.height - margin}
                                width={layout.innerBox.width}
                                height={margin}
                                fill={CLR_PAD}
                            />
                            <rect
                                x={layout.innerBox.x}
                                y={layout.innerBox.y + margin}
                                width={margin}
                                height={layout.innerBox.height - 2 * margin}
                                fill={CLR_PAD}
                            />
                            <rect
                                x={layout.innerBox.x + layout.innerBox.width - margin}
                                y={layout.innerBox.y + margin}
                                width={margin}
                                height={layout.innerBox.height - 2 * margin}
                                fill={CLR_PAD}
                            />
                        </g>
                    )}

                    {/* Per-row baselines */}
                    {layout.rowPlacements.map(p => (
                        <line
                            key={`bl-${p.index}`}
                            x1={layout.innerBox.x + p.x}
                            y1={layout.innerBox.y + p.baselineY}
                            x2={layout.innerBox.x + p.x + p.layout.totalWidth}
                            y2={layout.innerBox.y + p.baselineY}
                            stroke={ANNOTATION.dim}
                            strokeWidth={1}
                            strokeDasharray='4 6'
                            opacity={0.5}
                        />
                    ))}

                    {/* Diagonal line */}
                    <line
                        x1={0}
                        y1={0}
                        x2={layout.width}
                        y2={layout.height}
                        stroke={ANNOTATION.dim}
                        strokeWidth={1}
                        strokeDasharray='6 6'
                        opacity={0.4}
                    />

                    {/* Corner-radius indicator at top-right */}
                    <circle
                        cx={layout.width - layout.cornerRadius}
                        cy={layout.cornerRadius}
                        r={layout.cornerRadius}
                        fill='none'
                        stroke={ANNOTATION.radius}
                        strokeWidth={2}
                        strokeDasharray='4 4'
                        opacity={0.6}
                    />
                </g>

                {/* Outer W dimension (top, unscaled px) */}
                <HorizDim
                    x1={PAD}
                    x2={PAD + layout.width * SCALE}
                    y={PAD - 24}
                    label={`W = ${round4(layout.width)} mm`}
                    clr={ANNOTATION.dim}
                    labelOffset={8}
                />

                {!hideCallouts && (
                    <Callouts
                        layout={layout}
                        bgColor={bgColor}
                    />
                )}

                {/* Outer H dimension (left, unscaled px) */}
                <VertDim
                    x={PAD - 24}
                    y1={PAD}
                    y2={PAD + layout.height * SCALE}
                    label={`H = ${round4(layout.height)} mm`}
                    clr={ANNOTATION.dim}
                    labelOffset={10}
                />
            </svg>
        </div>
    )
}

interface CalloutsProps {
    readonly layout: BackgroundLayout
    readonly bgColor: BgColor
}

function Callouts({layout, bgColor}: CalloutsProps) {
    const xRight = PAD + layout.width * SCALE
    const yTop = PAD
    const yBottom = yTop + layout.height * SCALE
    const labelX = xRight + 18
    const rArc = layout.cornerRadius * SCALE
    const arcCx = xRight - rArc
    const arcCy = yTop + rArc
    const theta = -Math.PI / 4
    const arcPx = arcCx + rArc * Math.cos(theta)
    const arcPy = arcCy + rArc * Math.sin(theta)

    const midY = yTop + (layout.height * SCALE) / 2
    const midX = PAD + (layout.width * SCALE) / 2
    const cbHalf = (layout.contrastBorder * SCALE) / 2
    const beHalf = (layout.blackEdge * SCALE) / 2
    const cbTargetX = xRight - cbHalf
    const beTargetX = xRight - layout.contrastBorder * SCALE - beHalf

    const rLabelY = yTop - 6
    const cbLabelY = midY - 10
    const beLabelY = midY + 12
    const dLabelY = yBottom - 6
    const dm = (layout.diagonal / 1000).toFixed(3)

    return (
        <g>
            <line
                x1={arcPx}
                y1={arcPy}
                x2={labelX - 4}
                y2={rLabelY}
                stroke={ANNOTATION.radius}
                strokeWidth={0.8}
                strokeDasharray='3 3'
            />
            <text
                x={labelX}
                y={rLabelY}
                dominantBaseline='middle'
                fontSize={11}
                fontFamily='ui-monospace, monospace'
                fill={ANNOTATION.radius}
            >
                R = {layout.cornerRadius} mm ({lvsRef('9.16', 'table')})
            </text>

            <line
                x1={cbTargetX}
                y1={midY}
                x2={labelX - 4}
                y2={cbLabelY}
                stroke={ANNOTATION.ok}
                strokeWidth={0.8}
                strokeDasharray='3 3'
            />
            <text
                x={labelX}
                y={cbLabelY}
                dominantBaseline='middle'
                fontSize={11}
                fontFamily='ui-monospace, monospace'
                fill={ANNOTATION.ok}
            >
                Kontrastapmale = {layout.contrastBorder} mm ({lvsRef('9.13', 'clause')},{' '}
                {lvsRef(bgColor === 'white' ? '9.15' : '9.14', 'table')})
            </text>

            {layout.blackEdge > 0 && (
                <>
                    <line
                        x1={beTargetX}
                        y1={midY}
                        x2={labelX - 4}
                        y2={beLabelY}
                        stroke={ANNOTATION.warn}
                        strokeWidth={0.8}
                        strokeDasharray='3 3'
                    />
                    <text
                        x={labelX}
                        y={beLabelY}
                        dominantBaseline='middle'
                        fontSize={11}
                        fontFamily='ui-monospace, monospace'
                        fill={ANNOTATION.warn}
                    >
                        Melnā mala = {layout.blackEdge} mm ({lvsRef('9.14', 'clause')},{' '}
                        {lvsRef('9.15', 'table')})
                    </text>
                </>
            )}

            <line
                x1={midX}
                y1={midY}
                x2={labelX - 4}
                y2={dLabelY}
                stroke={ANNOTATION.dim}
                strokeWidth={0.8}
                strokeDasharray='3 3'
            />
            <text
                x={labelX}
                y={dLabelY}
                dominantBaseline='middle'
                fontSize={11}
                fontFamily='ui-monospace, monospace'
                fill={ANNOTATION.dim}
            >
                D = {round4(layout.diagonal)} mm ({dm} m)
            </text>
        </g>
    )
}
